import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { usePresence } from '../hooks/usePresence';
import { CheckIcon, ChevronUpDownIcon } from './Icons';

const EXIT_MS = 180;
const GAP = 6;
const EDGE = 12;
const MAX_LIST = 320;
const TYPEAHEAD_RESET_MS = 700;

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  /** Trailing secondary text, e.g. a dialing code. */
  detail?: string;
}

interface Props<T extends string> {
  id?: string;
  value: T;
  options: readonly SelectOption<T>[];
  onChange: (value: T) => void;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  disabled?: boolean;
  /** What the trigger shows; defaults to the selected option's label. */
  display?: ReactNode;
  className?: string;
}

interface Placement {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
  above: boolean;
}

function place(trigger: DOMRect, listHeight: number): Placement {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(Math.max(trigger.width, 232), vw - EDGE * 2);
  const left = Math.max(EDGE, Math.min(trigger.left, vw - width - EDGE));
  const spaceBelow = vh - trigger.bottom - GAP - EDGE;
  const spaceAbove = trigger.top - GAP - EDGE;
  const wanted = Math.min(listHeight, MAX_LIST);
  const above = spaceBelow < wanted && spaceAbove > spaceBelow;
  const maxHeight = Math.max(120, Math.min(MAX_LIST, above ? spaceAbove : spaceBelow));
  return above
    ? { bottom: vh - trigger.top + GAP, left, width, maxHeight, above }
    : { top: trigger.bottom + GAP, left, width, maxHeight, above };
}

/** iOS pop-up button: a pill trigger that opens a material menu with a check on the current choice. */
export function Select<T extends string>({
  id,
  value,
  options,
  onChange,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  disabled,
  display,
  className = '',
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const { mounted, shown } = usePresence(open, EXIT_MS);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const typeahead = useRef({ text: '', at: 0 });
  const listId = useId();
  const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value));
  const selected = options[selectedIndex];

  const reposition = useCallback(() => {
    const button = buttonRef.current;
    const list = listRef.current;
    if (!button) return;
    setPlacement(place(button.getBoundingClientRect(), list?.scrollHeight ?? MAX_LIST));
  }, []);

  useLayoutEffect(() => {
    if (!mounted) return;
    reposition();
    const list = listRef.current;
    const item = itemRefs.current[selectedIndex];
    if (list && item) list.scrollTop = item.offsetTop - list.clientHeight / 2 + item.offsetHeight / 2;
    // Placement is measured once per presentation, then kept in step with scroll and resize.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  useEffect(() => {
    if (!open || !mounted) return;
    itemRefs.current[active]?.focus({ preventScroll: true });
  }, [open, mounted, active]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!listRef.current?.contains(target) && !buttonRef.current?.contains(target)) setOpen(false);
    };
    const onScroll = (e: Event) => {
      if (listRef.current?.contains(e.target as Node)) return;
      reposition();
    };
    document.addEventListener('pointerdown', onPointer);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  const openMenu = () => {
    if (disabled) return;
    setActive(selectedIndex);
    setOpen(true);
  };

  const close = (restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  };

  const choose = (option: SelectOption<T>) => {
    if (option.value !== value) onChange(option.value);
    close(true);
  };

  const jumpTo = (key: string) => {
    const now = Date.now();
    const state = typeahead.current;
    state.text = now - state.at > TYPEAHEAD_RESET_MS ? key : state.text + key;
    state.at = now;
    const query = state.text.toLowerCase();
    const match = options.findIndex((o) => o.label.toLowerCase().startsWith(query));
    if (match >= 0) setActive(match);
  };

  const last = options.length - 1;

  return (
    <>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel ? `${ariaLabel}: ${selected?.label ?? ''}` : undefined}
        aria-invalid={ariaInvalid || undefined}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            openMenu();
          }
        }}
        className={`ios-select pressable ${open ? 'is-open' : ''} ${className}`}
      >
        <span className="min-w-0 flex-1 truncate text-left">{display ?? selected?.label}</span>
        <ChevronUpDownIcon className="h-4 w-4 shrink-0 opacity-60" />
      </button>
      {mounted &&
        createPortal(
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            data-lenis-prevent
            className="ios-menu fixed overflow-y-auto overscroll-contain py-1"
            style={{
              top: placement?.top,
              bottom: placement?.bottom,
              left: placement?.left ?? -9999,
              width: placement?.width,
              maxHeight: placement?.maxHeight ?? MAX_LIST,
              transformOrigin: placement?.above ? '24px 100%' : '24px 0',
              opacity: shown && placement ? 1 : 0,
              transform: shown && placement ? 'none' : `scale(0.72) translateY(${placement?.above ? 6 : -6}px)`,
              transition: shown
                ? 'transform 460ms var(--ease-spring), opacity 180ms var(--ease-standard)'
                : `transform ${EXIT_MS}ms var(--ease-standard), opacity ${EXIT_MS}ms var(--ease-standard)`,
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((i) => Math.min(i + 1, last));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((i) => Math.max(i - 1, 0));
              } else if (e.key === 'Home') {
                e.preventDefault();
                setActive(0);
              } else if (e.key === 'End') {
                e.preventDefault();
                setActive(last);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                close(true);
              } else if (e.key === 'Tab') {
                close(false);
              } else if (e.key.length === 1 && /\S/.test(e.key) && !e.metaKey && !e.ctrlKey && !e.altKey) {
                jumpTo(e.key);
              }
            }}
          >
            {options.map((option, i) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={i === active ? 0 : -1}
                  onClick={() => choose(option)}
                  onPointerMove={() => i !== active && setActive(i)}
                  className="ios-menu-item"
                >
                  <span className="flex w-4 shrink-0 justify-center">
                    {isSelected && <CheckIcon className="h-4 w-4 text-primary" />}
                  </span>
                  <span className={`min-w-0 flex-1 truncate ${isSelected ? 'font-semibold' : ''}`}>{option.label}</span>
                  {option.detail && <span className="shrink-0 tabular-nums text-muted">{option.detail}</span>}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}
